/**
 * EingangspruefungPage – normaler Olympus-Arbeitsweg fuer Scan-Dokumente im Status PRUEFEN (08.10.2026).
 * Eindeutige Faelle schliessen automatisch ab; hier erscheinen NUR die konkreten offenen Fragen. Die Seite enthaelt keine Fachlogik:
 * Entscheidungen laufen ueber /api/eingangspruefung (bestehende, serverseitig validierte Schreibpfade). Abgeschlossen + archiviert wird
 * ausschliesslich nach bestandenem Completion Gate durch den ScanDriveService.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, RefreshCw, CheckCircle2, AlertTriangle, ExternalLink, Wand2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { betreuungAPI } from '@/api/betreuungAPI';
import { authAPI } from '@/api/authAPI';
import { holePreviewUrl } from '@/lib/downloadTicket';
import {
  ACTION_LABEL, baueBody, bereinigeBeiKlientenwechsel, dokPrioritaet, EFFEKT_LABEL, filtere, grundText, hauptVorschlaege, istRetarget, optionenFuer, retargetId,
  standardEntscheidungen, teilText, volltextText, ART_LABEL, KEINE_FRIST_GRUND, LAGE_LABEL, WIDERSPRUCH_TEXT, dateiReviews, entscheidbar, bestaetigungsText,
  fristText, gesperrt,
} from './eingangspruefung/eingangspruefung';
import NeuanlageAnsicht from './eingangspruefung/NeuanlageAnsicht';
import { ART_TEXT, nbBadgeText, nbZuerst, konfliktText, holeVorschlag } from './eingangspruefung/neueBetreuung';

const PRIO_FARBE = { dringend: 'bg-red-600 text-white border-red-600', hoch: 'bg-red-50 text-red-800 border-red-300', mittel: 'bg-slate-50 text-slate-700 border-slate-300', niedrig: 'bg-slate-50 text-slate-500 border-slate-200' };

function KlientWahl({ aktuell, wert, onWaehlen }) {
  const [q, setQ] = useState('');
  const [treffer, setTreffer] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setTreffer([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        const r = await betreuungAPI.listKlienten({ search: q.trim() });
        setTreffer((Array.isArray(r) ? r : (r.klienten || [])).slice(0, 6));
      } catch { setTreffer([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="text-sm" data-testid="ep-klient-wahl">
      <div className="text-slate-500 mb-1">Klient: <strong>{wert ? wert.name : (aktuell ? aktuell.name : 'nicht zugeordnet')}</strong>{wert && ' (neu gewählt)'}</div>
      <Input placeholder="Anderen Klienten suchen …" value={q} onChange={(e) => setQ(e.target.value)} className="h-8 max-w-xs" data-testid="ep-klient-suche" />
      {treffer.length > 0 && (
        <div className="border rounded mt-1 max-w-xs bg-white">
          {treffer.map((k) => {
            const name = `${k.vorname || ''} ${k.nachname || ''}`.trim();
            return <button key={k.id} type="button" className="block w-full text-left px-2 py-1 hover:bg-violet-50" data-testid="ep-klient-treffer"
              onClick={() => { onWaehlen({ id: k.id, name }); setQ(''); setTreffer([]); }}>{name}</button>;
          })}
        </div>
      )}
    </div>
  );
}

function Beleg({ x }) {
  if (!x.beleg) return null;
  return <div className="text-xs text-slate-600 italic">„{x.beleg}“ {x.seite ? `(Seite ${x.seite})` : (x.beleg_gefunden ? '(Seite nicht bestimmbar)' : '(Beleg im Original nicht gefunden)')}</div>;
}

/** Alle Pruefinformationen der Nachanalyse und der Datei-Reviews -- vor jeder Entscheidung sichtbar (18.10.2026). */
function PruefInfo({ dok }) {
  const na = dok.nachanalyse;
  const d = na && na.details;
  const reviews = dateiReviews(dok);
  if (!na && !reviews.length) return null;
  return (
    <div className="border border-violet-200 bg-violet-50 rounded p-2 text-sm space-y-2" data-testid="ep-pruefinfo">
      <div className="font-medium text-violet-900">Prüfinformationen{na ? ` – Nachanalyse v${na.version}${na.vollstaendig === false ? ' (UNVOLLSTÄNDIG)' : ''}${na.gesperrt ? ' – gesperrt, keine Fristvorschläge' : ''}` : ''}</div>
      {na && na.details_fehlen && (
        <div className="text-red-800 font-medium" data-testid="ep-pruefinfo-unvollstaendig">Diese ältere Nachanalyse enthält keine Belege und keine Seitenangaben. Bitte das Original vollständig lesen.</div>
      )}
      {d && (
        <>
          {d.fristen.length > 0 && (
            <div data-testid="ep-na-fristen"><div className="font-medium">Fristen (vorschlagsfähig)</div>
              {d.fristen.map((x) => (
                <div key={`${x.datum}-${x.art}`} className="pl-2 border-l-2 border-violet-300 my-1">
                  <div>{x.datum} – {ART_LABEL[x.art] || x.art}{x.lage ? ` (${LAGE_LABEL[x.lage] || x.lage})` : ''}: {x.handlung}</div>
                  <Beleg x={x} />
                  {x.abgleich && x.abgleich.erfasst && <div className="text-xs text-green-800">Bereits in der Akte: {x.abgleich.treffer.map((t) => `${t.typ} „${t.text}“ (${t.datum}, ${t.status || '–'})`).join('; ')} – kein neuer Vorschlag</div>}
                  {x.abgleich && x.abgleich.abweichung && x.abgleich.abweichung.length > 0 && <div className="text-xs text-orange-800">Gleiche Sache mit anderem Datum im Bestand (kein neuer Vorschlag, Bestand prüfen): {x.abgleich.abweichung.map((t) => `${t.typ} „${t.text}“ (${t.datum})`).join('; ')}</div>}
                  {x.abgleich && x.abgleich.moeglich && x.abgleich.moeglich.length > 0 && <div className="text-xs text-amber-800" data-testid="ep-na-moeglich">Möglicherweise bereits erfasst – prüfen, ob dieselbe Sache (Vorschlag ggf. verwerfen): {x.abgleich.moeglich.map((t) => `${t.typ} „${t.text}“ (${t.datum})`).join('; ')}</div>}
                </div>
              ))}
            </div>
          )}
          {d.handlungspflichten.length > 0 && (
            <div data-testid="ep-na-pflichten"><div className="font-medium">Handlungspflichten</div>
              <ul className="list-disc pl-5">{d.handlungspflichten.map((h) => (
                <li key={h.text}>{h.text}{h.seite ? ` (Seite ${h.seite})` : ''}
                  {h.akte && h.akte.length > 0 && <span className="text-xs text-green-800"> – passend in der Akte: {h.akte.map((t) => `${t.typ} „${t.text}“ (${t.status || '–'})`).join('; ')}</span>}
                </li>))}
              </ul>
            </div>
          )}
          {d.hinweise.length > 0 && (
            <div data-testid="ep-na-hinweise"><div className="font-medium">Datumsangaben ohne Frist (nur zur Information)</div>
              <ul className="list-disc pl-5 text-slate-700">{d.hinweise.map((x, i) => (x.art === 'fristsignal'
                ? <li key={`sig-${i}`} className="text-orange-800 font-medium" data-testid="ep-na-fristsignal">{ART_LABEL.fristsignal}{x.seite ? ` (Seite ${x.seite})` : ''}: „{x.beleg}“ – {KEINE_FRIST_GRUND.von_ki_nicht_bewertet}</li>
                : <li key={`${x.datum}-${x.art}-${i}`}>{x.datum} – {ART_LABEL[x.art] || x.art}: {KEINE_FRIST_GRUND[x.grund] || x.grund}{x.handlung ? ` (${x.handlung})` : ''}</li>))}</ul>
            </div>
          )}
          {(d.widersprueche.length > 0 || d.personen.some((p) => p.konflikt)) && (
            <div className="text-red-800" data-testid="ep-na-widersprueche"><div className="font-medium">Widersprüche / Zuordnung</div>
              <ul className="list-disc pl-5">{d.widersprueche.map((w) => <li key={w.text}>{WIDERSPRUCH_TEXT[w.art] ? `${WIDERSPRUCH_TEXT[w.art]}: ` : ''}{w.text}</li>)}</ul>
            </div>
          )}
          {d.personen.length > 0 && (
            <div className="text-xs text-slate-600" data-testid="ep-na-personen">Genannte Personen: {d.personen.map((p) => `${p.name} (${p.grund})`).join('; ')}</div>
          )}
          {d.abweichungen.length > 0 && (
            <div data-testid="ep-na-abweichungen"><div className="font-medium">Abweichungen zur früheren Analyse</div>
              <ul className="list-disc pl-5">{d.abweichungen.map((a) => <li key={a}>{a}</li>)}</ul>
            </div>
          )}
        </>
      )}
      {reviews.map((v) => (
        <details key={v.review_id} open={!d} data-testid="ep-datei-review"><summary className="cursor-pointer text-violet-800">Vollständiger Text des Datei-Reviews</summary>
          <pre className="whitespace-pre-wrap text-xs text-slate-700">{v.begruendung}</pre>
        </details>
      ))}
    </div>
  );
}

function DokumentKarte({ dok, onFertig, nbAktiv, nbVorschlag, nbUnsicher, nbKonflikt, onNeuanlage }) {
  const [entsch, setEntsch] = useState(() => standardEntscheidungen(dok));
  const [klient, setKlient] = useState(null);
  const [laeuft, setLaeuft] = useState(false);
  const [ergebnis, setErgebnis] = useState(null);
  const [aufgaben, setAufgaben] = useState(null);
  const [nbLaeuft, setNbLaeuft] = useState(false);
  const [neu, setNeu] = useState(null);
  const [gelesen, setGelesen] = useState(false);
  const vorschlaege = hauptVorschlaege(dok);
  const darf = entscheidbar(dok, gelesen);
  const wirksam = bereinigeBeiKlientenwechsel(dok, entsch, klient && klient.id);

  const prio = dokPrioritaet(dok);
  const original = async () => {
    try {
      if (dok.ohne_drive) {
        // Dokumente ohne eigenen Drive-Eintrag (z. B. getrennte Teile): normaler Dokument-Viewer mit kurzlebigem Ticket
        const url = await holePreviewUrl(`/api/betreuung/dokumente/${dok.dokument_id}/download-ticket`, `/api/betreuung/dokumente/${dok.dokument_id}/download`, authAPI.getAuthHeaders());
        window.open(url, '_blank', 'noopener');
        return;
      }
      const r = await betreuungAPI.eingangspruefungOriginalLink(dok.dokument_id); window.open(r.viewer_url, '_blank', 'noopener');
    } catch (e) { toast.error(e.message); }
  };
  const nbPruefen = async () => {
    setNbLaeuft(true);
    try {
      // Qwen wertet im Hintergrund aus -- die Seite bleibt bedienbar, die Karte zeigt den Bearbeitungsstatus
      const v = await holeVorschlag((id) => betreuungAPI.neueBetreuungVorschlag(id), dok.dokument_id);
      if (v) onNeuanlage({ ...v, dokument: { titel: dok.titel, datum: dok.datum, zusammenfassung: dok.zusammenfassung }, zuordnung_konflikt: v.zuordnung_konflikt || nbKonflikt || null });
    } catch (e) { toast.error(e.message); }
    setNbLaeuft(false);
  };
  const aufgabenLaden = async () => {
    if (aufgaben) return;
    try { setAufgaben((await betreuungAPI.eingangspruefungAufgaben(dok.dokument_id)).aufgaben); } catch (e) { toast.error(e.message); }
  };
  const senden = async (keineHandlung) => {
    setLaeuft(true);
    try {
      const r = await betreuungAPI.eingangspruefungEntscheiden(dok.dokument_id, baueBody({ entscheidungen: wirksam, klientId: klient && klient.id, keineHandlung, neueAufgabe: neu, pruefinfoBestaetigt: gelesen }));
      setErgebnis(r);
      if (r.completion === 'PASS') { toast.success('Erledigt – wird beim nächsten Abgleich archiviert.'); onFertig(); }
      else toast.warning('Entscheidung erfasst, es fehlt noch etwas (siehe Karte).');
    } catch (e) { toast.error(e.message); }
    setLaeuft(false);
  };

  return (
    <div className="border rounded-lg bg-white p-4 space-y-3" data-testid="ep-karte" data-klasse={dok.klasse}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="font-medium text-slate-800">{dok.titel || 'Dokument'}</div>
          <div className="text-xs text-slate-500">{[dok.absender, dok.datum, dok.kategorie, dok.eingang && `Eingang ${dok.eingang}`].filter(Boolean).join(' · ')}</div>
        </div>
        <div className="flex gap-1">
          {nbVorschlag && <Badge className="bg-violet-600 text-white border-violet-600" data-testid="nb-badge">{nbBadgeText(nbVorschlag)}</Badge>}
          {prio && <Badge variant="outline" className={PRIO_FARBE[prio]} data-testid="ep-prioritaet">Priorität {prio}</Badge>}
          <Badge variant="outline" className={dok.klasse === 'AUTO' ? 'bg-green-50 text-green-800 border-green-300' : 'bg-amber-50 text-amber-800 border-amber-300'}>
            {dok.klasse === 'AUTO' ? 'Eindeutig' : 'Entscheidung nötig'}
          </Badge>
        </div>
      </div>
      {dok.teil && <div className="text-xs text-violet-800" data-testid="ep-teil">{teilText(dok.teil)}</div>}
      {dok.pruefhinweis && (
        <div className="border border-red-200 bg-red-50 rounded p-2 text-sm" data-testid="ep-pruefhinweis">
          <div className="font-medium text-red-800">Prüfhinweis{dok.pruefhinweis.art ? `: ${String(dok.pruefhinweis.art).replace(/_/g, ' ')}` : ''}{dok.pruefhinweis.status === 'geprueft' ? ' (bestätigt)' : ''}</div>
          <ul className="list-disc pl-5 text-red-900">{dok.pruefhinweis.zeilen.map((z) => <li key={z}>{z}</li>)}</ul>
        </div>
      )}
      {dok.volltext && !dok.volltext.bestaetigt && (
        <div className="text-sm text-orange-800 flex items-center gap-1" data-testid="ep-volltext"><AlertTriangle size={14} /> {volltextText(dok.volltext)} Bitte das Original prüfen.</div>
      )}
      {dok.zusammenfassung && <p className="text-sm text-slate-600">{dok.zusammenfassung}</p>}
      <PruefInfo dok={dok} />
      {dok.gruende.length > 0 && (
        <ul className="text-sm text-amber-800 list-disc pl-5" data-testid="ep-gruende">{dok.gruende.map((g) => <li key={g}>{grundText(g)}</li>)}</ul>
      )}
      {dok.completion_blockiert && (
        <div className="text-xs text-orange-700 flex items-center gap-1" data-testid="ep-blockiert"><AlertTriangle size={12} />
          Entscheidung liegt vor, Abschluss blockiert – es fehlt: {(dok.completion_blockiert.fehlende_effekte || []).map((e) => EFFEKT_LABEL[e] || e).join(', ') || dok.completion_blockiert.grund}
        </div>
      )}
      {nbAktiv && nbKonflikt && (
        <div className="border border-red-300 bg-red-50 rounded p-2 text-sm text-red-900 flex items-start gap-1" data-testid="nb-konflikt"><AlertTriangle size={14} className="mt-0.5" />
          <span><strong>Zuordnung widersprüchlich:</strong> {konfliktText(nbKonflikt)} Die Zuordnung wurde nicht verändert – bitte korrigieren:
            anderen Klienten wählen oder die Neuanlage in der eigenen Ansicht prüfen. Aufgaben- und Fristvorschläge dieses Dokuments können nur verworfen werden.</span></div>
      )}
      <KlientWahl aktuell={dok.klient} wert={klient} onWaehlen={setKlient} />
      {nbAktiv && (!dok.klient || nbKonflikt) && !klient && nbUnsicher && !nbVorschlag && nbUnsicher.ergebnis === 'in_bearbeitung' && (
        <div className="text-sm text-violet-800 flex items-center gap-1" data-testid="nb-ki-laeuft-auto"><Loader2 size={14} className="animate-spin" />
          Möglicher Betreuungsbeschluss – Qwen wertet ihn im Hintergrund aus.</div>
      )}
      {nbAktiv && (!dok.klient || nbKonflikt) && !klient && nbUnsicher && !nbVorschlag && nbUnsicher.ergebnis !== 'in_bearbeitung' && (
        <div className="text-sm text-amber-800 flex items-center gap-1" data-testid="nb-unsicher"><AlertTriangle size={14} />
          Möglicher Betreuungsbeschluss – automatische Vorbereitung unsicher ({nbUnsicher.grund || 'bitte prüfen'}). Bitte manuell prüfen.</div>
      )}
      {nbAktiv && (!dok.klient || nbKonflikt) && !klient && (
        nbVorschlag
          ? <Button size="sm" className="bg-violet-600 hover:bg-violet-700" onClick={() => onNeuanlage(nbVorschlag)} data-testid="nb-karte-oeffnen">Neuanlage-Ansicht öffnen</Button>
          : nbLaeuft
            ? <span className="text-sm text-violet-800 flex items-center gap-1" data-testid="nb-ki-laeuft"><Loader2 size={14} className="animate-spin" /> Qwen wertet den Beschluss aus …</span>
            : <Button size="sm" variant="outline" onClick={nbPruefen} data-testid="nb-pruefen">Neue Betreuung aus Beschluss prüfen</Button>
      )}
      {vorschlaege.length > 0 && (
        <div className="space-y-2" data-testid="ep-vorschlaege">
          {vorschlaege.map((v) => (
            <div key={v.review_id} className="border rounded p-2 text-sm bg-slate-50">
              <div className="font-medium">{ACTION_LABEL[v.action] || v.action}: {v.titel || (v.ziel_titel && `→ ${v.ziel_titel}`)}</div>
              <div className="text-xs text-slate-500" data-testid="ep-vorschlag-meta">{[v.ziel_titel && v.action === 'UPDATE' && `Ziel: ${v.ziel_titel}`, fristText(v), v.prioritaet && `Priorität ${v.prioritaet}`].filter(Boolean).join(' · ')}</div>
              {v.begruendung && v.quelle === 'nachanalyse_20261017' && <div className="text-xs text-slate-600" data-testid="ep-vorschlag-beleg">{v.begruendung}</div>}
              {v.hinweise.length > 0 && <div className="text-xs text-amber-700">{v.hinweise.map(grundText).join(' · ')}{v.aehnliche_aufgabe ? ` (${v.aehnliche_aufgabe})` : ''}</div>}
              <div className="mt-1 flex gap-3">
                {optionenFuer(v).map(([w, label]) => (
                  <label key={w} className="flex items-center gap-1 cursor-pointer">
                    <input type="radio" name={`ep-${v.review_id}`} checked={wirksam[v.review_id] === w} disabled={!!klient && w !== 'REJECTED'}
                      onChange={() => setEntsch((p) => ({ ...p, [v.review_id]: w }))} data-testid={`ep-${w === 'REJECTED' ? 'verwerfen' : (w === 'APPROVED' ? 'uebernehmen' : 'ohne-frist')}`} />
                    {label}
                  </label>
                ))}
                <select className="border rounded px-1 py-0.5 text-xs max-w-[16rem]" data-testid="ep-andere-aufgabe" disabled={!!klient || !dok.klient || gesperrt(v)}
                  value={istRetarget(wirksam[v.review_id]) ? retargetId(wirksam[v.review_id]) : ''} onFocus={aufgabenLaden}
                  onChange={(e) => setEntsch((p) => ({ ...p, [v.review_id]: e.target.value ? `RETARGET:${e.target.value}` : 'REJECTED' }))}>
                  <option value="">Andere Aufgabe wählen …</option>
                  {(aufgaben || []).map((t) => <option key={t.id} value={t.id}>{(t.titel || '').slice(0, 70)}</option>)}
                </select>
              </div>
            </div>
          ))}
        </div>
      )}
      {dok.klient && (
        <div className="text-sm" data-testid="ep-neue-aufgabe">
          {!neu ? (
            <button type="button" className="text-violet-700 hover:underline" onClick={() => setNeu({ titel: '', prioritaet: 'mittel', faellig_am: '' })} data-testid="ep-neue-aufgabe-oeffnen">+ Neue Aufgabe anlegen</button>
          ) : (
            <div className="border rounded p-2 bg-slate-50 space-y-2">
              <Input placeholder="Titel der Aufgabe (mind. 5 Zeichen)" value={neu.titel} onChange={(e) => setNeu({ ...neu, titel: e.target.value })} className="h-8" data-testid="ep-neue-aufgabe-titel" />
              <div className="flex gap-2 items-center">
                <select className="border rounded px-1 py-1 text-sm" value={neu.prioritaet} onChange={(e) => setNeu({ ...neu, prioritaet: e.target.value })} data-testid="ep-neue-aufgabe-prio">
                  {['niedrig', 'mittel', 'hoch', 'dringend'].map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
                <Input type="date" value={neu.faellig_am} onChange={(e) => setNeu({ ...neu, faellig_am: e.target.value })} className="h-8 w-40" title="Frist (nur wenn im Schreiben genannt)" />
                <button type="button" className="text-slate-500 hover:underline" onClick={() => setNeu(null)}>abbrechen</button>
              </div>
            </div>
          )}
        </div>
      )}
      {ergebnis && ergebnis.completion === 'FAIL' && (
        <div className="text-sm text-orange-700" data-testid="ep-fehlt">Noch offen: {ergebnis.fehlende_effekte.map((e) => EFFEKT_LABEL[e] || e).join(', ')}</div>
      )}
      {dok.pruefinfo_bestaetigung_noetig && (
        <label className="flex items-start gap-2 text-sm text-violet-900" data-testid="ep-pruefinfo-bestaetigen">
          <input type="checkbox" checked={gelesen} onChange={(e) => setGelesen(e.target.checked)} className="mt-1" />
          <span>{bestaetigungsText(dok)}</span>
        </label>
      )}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={original} data-testid="ep-original"><ExternalLink size={14} className="mr-1" /> Original ansehen</Button>
        <Button size="sm" onClick={() => senden(false)} disabled={laeuft || !darf} data-testid="ep-abschliessen">{laeuft ? <Loader2 className="animate-spin" size={14} /> : <CheckCircle2 size={14} className="mr-1" />} Entscheidung speichern</Button>
        <Button size="sm" variant="outline" onClick={() => senden(true)} disabled={laeuft || !!klient || !darf} data-testid="ep-keine-handlung">Keine Handlung erforderlich</Button>
      </div>
    </div>
  );
}

export default function EingangspruefungPage() {
  const [daten, setDaten] = useState(null);
  const [filter, setFilter] = useState('human');
  const [lade, setLade] = useState(false);
  const [auto, setAuto] = useState(null);
  const [nbAktiv, setNbAktiv] = useState(false);
  useEffect(() => { betreuungAPI.neueBetreuungStatus().then((r) => setNbAktiv(!!r.aktiv)).catch(() => setNbAktiv(false)); }, []);
  const [nbOffen, setNbOffen] = useState({ vorschlaege: [], unsicher: [], konflikte: [] });
  useEffect(() => {
    if (!nbAktiv) return;
    betreuungAPI.neueBetreuungOffen().then((r) => setNbOffen({ vorschlaege: r.vorschlaege || [], unsicher: r.unsicher || [], konflikte: r.konflikte || [] })).catch(() => {});
  }, [nbAktiv, daten]);
  const nbVorschlagZu = useMemo(() => Object.fromEntries(nbOffen.vorschlaege.map((v) => [v.dokument_id, v])), [nbOffen]);
  const nbUnsicherZu = useMemo(() => Object.fromEntries(nbOffen.unsicher.map((u) => [u.dokument_id, u])), [nbOffen]);
  const nbKonfliktZu = useMemo(() => Object.fromEntries(nbOffen.konflikte.map((k) => [k.dokument_id, k])), [nbOffen]);
  const [nbAnsicht, setNbAnsicht] = useState(null);       // eigenstaendige Neuanlage-Ansicht (unabhaengig von der Dokumentliste)

  const laden = useCallback(async () => {
    setLade(true);
    try { setDaten(await betreuungAPI.eingangspruefungOffen()); } catch (e) { toast.error(e.message); }
    setLade(false);
  }, []);
  useEffect(() => { laden(); }, [laden]);

  const autoVorschau = async () => { try { setAuto(await betreuungAPI.eingangspruefungAuto(true)); } catch (e) { toast.error(e.message); } };
  const autoAusfuehren = async () => {
    try { const r = await betreuungAPI.eingangspruefungAuto(false); toast.success(`${r.angewendet.length} Dokument(e) abgeschlossen`); setAuto(null); laden(); } catch (e) { toast.error(e.message); }
  };
  const liste = useMemo(() => nbZuerst(filtere((daten && daten.dokumente) || [], filter), nbVorschlagZu), [daten, filter, nbVorschlagZu]);

  if (nbAktiv && nbAnsicht) {
    return (
      <div className="p-6 max-w-5xl mx-auto space-y-4" data-testid="eingangspruefung-page">
        <NeuanlageAnsicht key={nbAnsicht.id} start={nbAnsicht} onSchliessen={() => setNbAnsicht(null)} onFertig={() => { setNbAnsicht(null); laden(); }} />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-4" data-testid="eingangspruefung-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold">Eingangsprüfung</h1>
          <p className="text-sm text-slate-500">Scans, die noch eine Entscheidung brauchen. Eindeutige Fälle schließen automatisch ab.</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={autoVorschau} data-testid="ep-auto-vorschau"><Wand2 size={14} className="mr-1" /> Eindeutige prüfen</Button>
          <Button size="sm" variant="outline" onClick={laden} disabled={lade}><RefreshCw size={14} className={lade ? 'animate-spin' : ''} /></Button>
        </div>
      </div>
      {daten && (
        <div className="flex gap-2 text-sm" data-testid="ep-filter">
          {[['human', `Entscheidung nötig (${daten.human})`], ['auto', `Eindeutig (${daten.auto})`], ['alle', `Offen gesamt (${daten.human + daten.auto})`]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setFilter(k)} className={`px-3 py-1 rounded-full border ${filter === k ? 'bg-violet-600 text-white border-violet-600' : 'bg-white'}`}>{l}</button>
          ))}
        </div>
      )}
      {auto && (
        <div className="border rounded-lg p-3 bg-green-50 text-sm" data-testid="ep-auto-ergebnis">
          Vorschau: <strong>{auto.auto.length}</strong> von {auto.gesamt} Dokumenten sind eindeutig und können ohne Klick abgeschlossen werden; {auto.human.length} brauchen eine Entscheidung.
          {auto.auto.length > 0 && <Button size="sm" className="ml-3" onClick={autoAusfuehren} data-testid="ep-auto-ausfuehren">Jetzt abschließen</Button>}
        </div>
      )}
      {nbAktiv && nbOffen.vorschlaege.length > 0 && (
        <div className="border-2 border-violet-500 bg-violet-50 rounded-lg p-3 text-sm text-violet-900 flex items-center gap-2" data-testid="nb-banner">
          <div className="w-full space-y-2">
            <div><AlertTriangle size={16} className="inline mr-1" /> <strong>{nbOffen.vorschlaege.length} neue Betreuung{nbOffen.vorschlaege.length === 1 ? '' : 'en'} zur Bestätigung</strong>
              {' '}– aus eingegangenen Beschlüssen vorbereitet. Angelegt wird erst nach Ihrer Bestätigung.</div>
            {nbOffen.vorschlaege.map((v) => {
              const f = v.felder || {};
              const name = [f.vorname && f.vorname.wert, f.nachname && f.nachname.wert].filter(Boolean).join(' ');
              return (
                <div key={v.id} className="flex flex-wrap items-center justify-between gap-2 bg-white border border-violet-200 rounded p-2" data-testid="nb-eintrag">
                  <span>{v.veraltet ? 'Person: wird beim Öffnen neu ausgewertet' : (name || 'Person nicht erkannt')} · {ART_TEXT[v.art] || v.art}{(v.dokument || {}).titel ? ` · ${v.dokument.titel}` : ''}
                    {v.zuordnung_konflikt && <Badge variant="outline" className="ml-2 bg-red-50 text-red-800 border-red-300" data-testid="nb-eintrag-konflikt">Zuordnung widersprüchlich</Badge>}</span>
                  <Button size="sm" onClick={() => setNbAnsicht(v)} data-testid="nb-oeffnen">Neuanlage öffnen</Button>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {daten && daten.bereit_zur_archivierung > 0 && <p className="text-xs text-slate-500" data-testid="ep-bereit">{daten.bereit_zur_archivierung} Dokument(e) sind erledigt und werden beim nächsten Abgleich archiviert.</p>}
      {!daten && <Loader2 className="animate-spin text-slate-400" />}
      {daten && liste.length === 0 && <p className="text-sm text-slate-500" data-testid="ep-leer">Nichts offen.</p>}
      <div className="space-y-3">{liste.map((d) => <DokumentKarte key={d.dokument_id} dok={d} onFertig={laden} nbAktiv={nbAktiv} nbVorschlag={nbVorschlagZu[d.dokument_id]} nbUnsicher={nbUnsicherZu[d.dokument_id]} nbKonflikt={nbKonfliktZu[d.dokument_id]} onNeuanlage={setNbAnsicht} />)}</div>
    </div>
  );
}
